// THE LAST TEN, BACK BESIDE THE FORM (his "I think we need to restore most the lost things if not all", 1 Nov 2026 — the
// rebuild had dropped the old forms' "Last 10" lists, components/RecentEntries.tsx; "but with the improved UI and not the
// cluttered as the previous one"). One quiet list in the form's aside on the laptop: one line per entry — when · who · the
// figure — a line opens its entry (Money movements) or its deal. On the phone, one quiet "Last 10 →" row under the form
// (LastTenLink) opens the SAME list — the same read, the same filters — in a sheet: never a link out to a register,
// which cannot hold the list's scope (Trades opens on today over the desk's latest 200; Money movements cannot tell
// capital from drawings) — Astra, 1 Nov 2026.
//
// A DEAL OPENS HERE (a review, 1 Nov 2026): the Trades log resolves an open deal only from the desk's latest 200, so a
// quieter party's deal opened there to an empty log. The deal sheet opens on the form's own page from the row this list
// already holds (`?deal=<id>`, so Back closes it), as the party page does. An ENTRY keeps its link: Money movements
// finds it by its own read — `?q=<reference>` (searched over All time), or, with no reference, its kind on its day.
//
// Reads, refreshed by every posting's refreshMoneyViews: trades from the trades log filtered by the Worker — party,
// direction, pair, the ten newest that match (main 48ae76dc) — every row checked against the filters too; money from the
// movement ledger by kind and party, newest first. Pre-cutover replays (`legacy2:`, absorbed) and voided deals are left out.
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Direction, type MovementKind, type MovementRow, type TradeRow } from '../../../lib/api';
import { displayAsset, dubaiDate, fmtAmount, fmtRate, fmtStamp } from '../../../lib/format';
import { partyName, railName, sameAsset } from '../../../lib/naming';
import { DetailSheet, Pending, ReadFailed, TAP } from '../kit';
import { TradeDetail } from '../trades/TradeDetail';
import { AsideBlock } from './parts';

const LIMIT = 10;
/** A side of the owner's kind (capital in vs drawings) is paged for: 50 entries a read, at most 10 reads. */
const MOVE_PAGE = 50, MOVE_PAGES = 10;

export type LastTenProps =
  | { kind: 'trades'; partySlug?: string; direction?: Direction; pair?: { base: string; quote: string }; entity?: string }
  | { kind: 'money'; direction: 'in' | 'out'; partySlug?: string; entity?: string }
  | { kind: 'moves'; entity?: string }
  | { kind: 'entries'; movementKind: Extract<MovementKind, 'expense' | 'owner' | 'p2p'>; direction?: 'in' | 'out'; entity?: string };

/** How far back "All of them" (the laptop's way to the register) opens Money movements: the register opens on today otherwise, and the last ten
 *  of a kind are often older (Astra, 1 Nov 2026). */
export const LAST_TEN_DAYS = 90;

/** Where the whole list lives, with a period the register takes:
 *  - Money movements: the kind (money in / out, a cost, the owner's money, between parties, moves), the party, and
 *    from = 90 days back (`?from=`, its custom period). It has no filter for capital vs drawings — both show under the
 *    owner's kind.
 *  - Trades: only `?q=` — a party's slug opens All time on that party; it takes no period, direction or pair in its URL,
 *    so without a party it opens on today. */
export function lastTenHref(p: LastTenProps, now: number = Date.now()): string {
  if (p.kind === 'trades') return p.partySlug ? `/trades?q=${encodeURIComponent(p.partySlug)}` : '/trades';
  const kind = p.kind === 'money' ? (p.direction === 'in' ? 'money_in' : 'money_out') : p.kind === 'moves' ? 'rail_move' : p.movementKind;
  const party = p.kind === 'money' && p.partySlug ? `&party=${encodeURIComponent(p.partySlug)}` : '';
  return `/movements?kind=${kind}${party}&from=${dubaiDate(new Date(now - LAST_TEN_DAYS * 86_400_000))}`;
}

/** Where one entry opens: Money movements resolves `?entry=` from its own read, so the link names a read that holds it —
 *  its reference (a search runs over All time), else its kind on its own day. */
export function entryHref(m: Pick<MovementRow, 'txn_id' | 'reference' | 'kind' | 'at'>): string {
  const entry = `&entry=${encodeURIComponent(m.txn_id)}`;
  if (m.reference) return `/movements?q=${encodeURIComponent(m.reference)}${entry}`;
  const day = dubaiDate(new Date(m.at));
  return `/movements?kind=${encodeURIComponent(m.kind)}&from=${day}&to=${day}${entry}`;
}

const samePair = (t: { asset_take: string; asset_give: string }, pair: { base: string; quote: string }) =>
  (sameAsset(t.asset_take, pair.base) && sameAsset(t.asset_give, pair.quote)) || (sameAsset(t.asset_take, pair.quote) && sameAsset(t.asset_give, pair.base));

/** WHAT THE LIST READS, pure so it can be pinned: trades from the trades log FILTERED BY THE WORKER (party, direction,
 *  pair — main 48ae76dc, his Last 10 exactly: the ten newest that match, however quiet the party or old the deal); money
 *  from the movement ledger by kind and party, a side (capital in vs a drawing) kept apart by `side`. */
export function lastTenRead(p: LastTenProps): {
  source: 'trades' | 'movements'; party: string; kind?: MovementKind; side?: 'in' | 'out'; limit: number;
} {
  if (p.kind === 'trades') return { source: 'trades', party: p.partySlug ?? '', limit: LIMIT };
  const kind: MovementKind = p.kind === 'money' ? (p.direction === 'in' ? 'money_in' : 'money_out') : p.kind === 'entries' ? p.movementKind : 'rail_move';
  const side = p.kind === 'entries' ? p.direction : undefined;
  return { source: 'movements', party: p.kind === 'money' ? p.partySlug ?? '' : '', kind, side, limit: side ? MOVE_PAGE : LIMIT };
}

type MovePage = { rows: MovementRow[]; next_cursor?: string | null };
/** THE LAST TEN OF ONE SIDE, PAGED FOR (Astra, 1 Nov 2026): the ledger filters by kind, not by side, so twenty drawings
 *  in a row would hide every capital in behind a capped read. Read page after page until ten of the side are in hand or
 *  the history ends; `complete` is false only when the reads ran out first (the screen then says how far it looked). */
export async function readSide(page: (cursor?: string) => Promise<MovePage>, side: 'in' | 'out', maxPages = MOVE_PAGES):
  Promise<{ rows: MovementRow[]; complete: boolean; looked: number }> {
  const rows: MovementRow[] = [];
  let cursor: string | undefined, looked = 0;
  for (let i = 0; i < maxPages; i++) {
    const p = await page(cursor);
    looked += p.rows.length;
    rows.push(...p.rows.filter((m) => m.direction === side));
    if (rows.length >= LIMIT || !p.next_cursor) return { rows: rows.slice(0, LIMIT), complete: true, looked };
    cursor = p.next_cursor;
  }
  return { rows: rows.slice(0, LIMIT), complete: false, looked };
}

/** What an empty or short list may claim: the filtered trades read and a side paged to its end are whole, so "Nothing
 *  yet." is true; a Worker that ignored the filters (`unfiltered`: before 48ae76dc, it answered the desk's newest ten) and
 *  a side whose reads ran out first say how far they looked — never "Nothing yet" from a slice (Astra, 1 Nov 2026). */
export function lastTenScope(source: 'trades' | 'movements', o: { unfiltered?: boolean; complete?: boolean; looked?: number }):
  { empty: string; short: string | null } {
  if (source === 'trades') return o.unfiltered
    ? { empty: `None among the newest ${LIMIT} trades.`, short: `From the newest ${LIMIT} trades.` } : { empty: 'Nothing yet.', short: null };
  return o.complete === false
    ? { empty: `None in the last ${o.looked ?? 0} entries.`, short: `From the last ${o.looked ?? 0} entries.` } : { empty: 'Nothing yet.', short: null };
}

/** The ten deals the list shows: live ones (no pre-cutover replay, nothing voided) of the direction and pair, newest first. */
export function pickTrades(rows: readonly TradeRow[], p: { partySlug?: string; direction?: Direction; pair?: { base: string; quote: string } }): TradeRow[] {
  return rows
    .filter((t) => !t.deal_id.startsWith('legacy2:') && t.lifecycle !== 'voided' && t.lifecycle !== 'absorbed'
      && (!p.partySlug || t.party_slug === p.partySlug)
      && (!p.direction || t.direction === p.direction) && (!p.pair || samePair(t, p.pair)))
    .sort((a, b) => Date.parse(b.booked_at) - Date.parse(a.booked_at))
    .slice(0, LIMIT);
}

/** Who a money line names: the two accounts of a move, the account of a party's own list, both parties of a move between
 *  parties (source → destination: two transfers from one party to two others must not read alike), else the party. */
export function whoOf(props: LastTenProps, party: string, m: MovementRow): string {
  if (props.kind === 'moves') return `${m.rail ? railName(m.rail) : '—'} → ${m.counter_rail ? railName(m.counter_rail) : '—'}`;
  if (props.kind === 'money') return party ? (m.rail ? railName(m.rail) : '—') : (m.party ? partyName(m.party) : '—');
  if (props.kind === 'entries' && props.movementKind === 'p2p') return `${m.party ? partyName(m.party) : '—'} → ${m.counterparty ? partyName(m.counterparty) : '—'}`;
  return m.party ? partyName(m.party) : m.rail ? railName(m.rail) : '—';
}

/** The read and the lines, shared by the laptop's list and the phone's sheet (one cache entry: the same query keys). */
function useLastTen(props: LastTenProps, onOpenDeal: (id: string) => void) {
  const entity = props.entity ?? 'dubaiotc';
  const read = lastTenRead(props);
  const party = read.party;

  const tp = props.kind === 'trades' ? props : null;
  const trades = useQuery({
    queryKey: ['trades', 'last-ten', entity, party, tp?.direction ?? '', tp?.pair?.base ?? '', tp?.pair?.quote ?? ''],
    queryFn: () => api.lastTrades(entity, { party: party || undefined, direction: tp?.direction, base: tp?.pair?.base, quote: tp?.pair?.quote, pageSize: LIMIT }),
    enabled: read.source === 'trades', staleTime: 60_000,
  });
  const moves = useQuery({
    queryKey: ['movements', 'recent-ten', entity, read.kind, party, read.side ?? 'both'],
    queryFn: async () => {
      const page = (cursor?: string) => api.movements({ entity, kind: read.kind, party: party || undefined, limit: read.limit, cursor });
      if (read.side) return readSide(page, read.side);
      const p = await page();
      return { rows: p.rows.slice(0, LIMIT), complete: true, looked: p.rows.length };
    },
    enabled: read.source === 'movements', staleTime: 60_000,
  });
  const q = read.source === 'trades' ? trades : moves;

  const all: TradeRow[] = read.source === 'trades' ? trades.data?.trades ?? [] : [];
  // EVERY ROW IS CHECKED against the filters: a Worker before 48ae76dc ignores them and answers the desk's newest ten.
  const picked = props.kind === 'trades' ? pickTrades(all, props) : [];
  const unfiltered = picked.length < all.length;

  let lines: ReactNode[] = [];
  if (q.data && props.kind === 'trades') {
    lines = picked.map((t) => {
      const buys = t.direction === 'customer_buy';
      const coin = buys ? t.asset_give : t.asset_take, amt = buys ? t.amount_give : t.amount_take;
      const fiat = buys ? t.asset_take : t.asset_give;
      return (
        <Line key={t.deal_id} onOpen={() => onOpenDeal(t.deal_id)}
          at={t.booked_at} who={party ? (buys ? 'Buys' : 'Sells') : (t.party_name ?? t.party_slug ?? '—')}
          figure={<>{fmtAmount(amt, coin)}&nbsp;<span className="text-meta text-tg-hint">{displayAsset(coin)}</span></>}
          tail={<>at <span className="font-mono tabular">{fmtRate(t.rate, fiat)}</span></>} />
      );
    });
  } else if (q.data) {
    lines = (moves.data?.rows ?? []).map((m) => (
      <Line key={m.txn_id} to={entryHref(m)} at={m.at} who={whoOf(props, party, m)}
        figure={<>{fmtAmount(m.amount, m.asset)}&nbsp;<span className="text-meta text-tg-hint">{displayAsset(m.asset)}</span></>} />
    ));
  }

  const scope = lastTenScope(read.source, { unfiltered, complete: moves.data?.complete, looked: moves.data?.looked });
  const list = q.isError ? <ReadFailed what="this list" error={q.error} onRetry={() => void q.refetch()} />
    : !q.data ? <p className="text-meta"><Pending inline /></p>
    // An empty answer is said only from a read that is not being refreshed — after a posting the old read must not claim
    // "Nothing yet" beside the receipt — and from a capped read it says how far it looked (Astra, 1 Nov 2026).
    : lines.length === 0 ? (q.isFetching ? <p className="text-meta"><Pending inline /></p> : <p className="text-meta text-tg-hint">{scope.empty}</p>)
    : <>
        <ul className="divide-y divide-stroke-soft border-y border-stroke-soft">{lines}</ul>
        {lines.length < LIMIT && scope.short && <p className="mt-2 text-meta text-tg-hint">{scope.short}</p>}
      </>;
  return { q, all, lines, list };
}

/** The list, for the form's aside (laptop). A deal opens its sheet here (`?deal=`) — unless the phone's sheet holds it. */
export function LastTen({ title, ...p }: LastTenProps & { title: string }) {
  const props = p as LastTenProps;
  const [search, setSearch] = useSearchParams();
  const openDealSheet = (id: string) => { const n = new URLSearchParams(search); n.set('deal', id); setSearch(n); };
  const closeDeal = () => { const n = new URLSearchParams(search); n.delete('deal'); setSearch(n, { replace: true }); };
  const { q, all, lines, list } = useLastTen(props, openDealSheet);
  const dealOpen = props.kind === 'trades' && !search.has(SHEET) ? search.get('deal') : null;
  const openDeal = dealOpen ? all.find((t) => t.deal_id === dealOpen) ?? null : null;

  return (
    <AsideBlock title={splitTitle(title).head} sub={splitTitle(title).sub}>
      {list}
      {q.data && lines.length > 0 && (
        <p className="mt-2"><Link to={lastTenHref(props)} className={`${TAP} text-meta`}>All of them →</Link></p>
      )}
      {openDeal && createPortal(<TradeDetail key={openDeal.deal_id} deal={openDeal} onClose={closeDeal} />, document.body)}
    </AsideBlock>
  );
}

/** The phone's sheet is open while the URL carries it, so Back closes it (and a deal opened from it, first). */
const SHEET = 'last10';

/** One entry as a FIXED THREE-PART ROW (1 Nov 2026 audit: "Buys / 18,400.5 USDT at 3.67" stacked in one cell): when at the
 *  left (meta, mono), who in the middle (body, up to two lines, the full name as its title), the figure at the right (mono,
 *  unit muted) with a deal's rate as a quiet second figure under it. One grid at every width — the side column is never
 *  narrower than 280 px now (it drops under the form below 1280), and the phone's sheet is a full-width column. */
function Line({ to, onOpen, at, who, figure, tail }: {
  to?: string; onOpen?: () => void; at: string; who: string; figure: ReactNode; tail?: ReactNode;
}) {
  const cls = 'grid min-h-11 w-full grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-3 py-2 text-left hover:bg-panel-2';
  const body = (
    <>
      <span className="whitespace-nowrap font-mono tabular text-meta text-tg-hint">{fmtStamp(at)}</span>
      <span className="line-clamp-2 min-w-0 break-words text-body text-tg-text" title={who}>{who}</span>
      <span className="whitespace-nowrap text-right font-mono tabular text-body text-tg-text">{figure}</span>
      {tail && <span className="col-start-3 whitespace-nowrap text-right text-meta text-tg-hint">{tail}</span>}
    </>
  );
  return <li>{to ? <Link to={to} className={cls}>{body}</Link> : <button type="button" className={cls} onClick={onOpen}>{body}</button>}</li>;
}

/** "Last 10 buys of USDT with Cedar …" → the heading "Last 10" and the filter as one quiet line under it (1 Nov 2026 audit:
 *  a five-line title in the side column). */
export function splitTitle(title: string): { head: string; sub?: string } {
  const m = /^Last 10\s+(.+)$/.exec(title);
  const rest = m?.[1];
  return rest ? { head: 'Last 10', sub: rest.charAt(0).toUpperCase() + rest.slice(1) } : { head: title };
}

/** The phone's way to the same list: one quiet full-width 44 px row under the form (never sharing a touch area with
 *  About this job under it), which opens THE SAME list — the same read and filters as the laptop's aside — in a sheet.
 *  A deal tapped there opens its sheet in the list's place; Back returns to the list, then to the form. */
export function LastTenLink({ title, ...p }: LastTenProps & { title: string }) {
  const props = p as LastTenProps;
  const [search, setSearch] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const open = search.has(SHEET);
  // A step this row pushed is undone with Back; one the URL arrived with is taken off in place.
  const push = (set: (n: URLSearchParams) => void) => { const n = new URLSearchParams(search); set(n); setSearch(n, { state: { lastTenPushed: true } }); };
  const close = (key: string) => {
    if ((location.state as { lastTenPushed?: boolean } | null)?.lastTenPushed) navigate(-1);
    else { const n = new URLSearchParams(search); n.delete(key); setSearch(n, { replace: true }); }
  };
  return (
    <>
      <button type="button" onClick={() => push((n) => n.set(SHEET, '1'))}
        className="btn-link flex min-h-11 w-full items-center text-left text-meta xl:hidden">Last 10 →</button>
      {open && <LastTenSheet props={props} title={title} dealOpen={search.get('deal')}
        onOpenDeal={(id) => push((n) => n.set('deal', id))} onClose={() => close(SHEET)} onCloseDeal={() => close('deal')} />}
    </>
  );
}

function LastTenSheet({ props, title, dealOpen, onOpenDeal, onClose, onCloseDeal }: {
  props: LastTenProps; title: string; dealOpen: string | null;
  onOpenDeal: (id: string) => void; onClose: () => void; onCloseDeal: () => void;
}) {
  const { all, list } = useLastTen(props, onOpenDeal);
  const openDeal = props.kind === 'trades' && dealOpen ? all.find((t) => t.deal_id === dealOpen) ?? null : null;
  return createPortal(
    openDeal ? <TradeDetail key={openDeal.deal_id} deal={openDeal} onClose={onCloseDeal} />
      : <DetailSheet title={splitTitle(title).head} onClose={onClose}>
          {splitTitle(title).sub && <p className="mb-3 text-meta text-tg-hint">{splitTitle(title).sub}</p>}
          {list}
        </DetailSheet>,
    document.body,
  );
}
